import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../Providers/AuthContext';
import { submitBugReport } from '../../services/firebase/bugReportService';
import styles from './BugReportModal.module.css';

const PAGE_OPTIONS = [
  'Main Menu',
  'Challenge AI',
  'Multiplayer',
  'Tutorial',
  'Sandbox',
  'Puzzles',
  'Other',
];

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const { user, userProfile } = useContext(AuthContext);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [page, setPage] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Auto-close after success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        resetAndClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStepsToReproduce('');
    setPage('');
    setError('');
    setSuccess(false);
    setLoading(false);
  };

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Please enter a bug title');
      return;
    }
    if (!description.trim()) {
      setError('Please describe what happened');
      return;
    }

    setLoading(true);

    try {
      await submitBugReport({
        title: title.trim(),
        description: description.trim(),
        stepsToReproduce: stepsToReproduce.trim(),
        page: page || 'Not specified',
        reportedBy: user?.uid || 'anonymous',
        reportedByUsername: userProfile?.username || 'Anonymous',
        userAgent: navigator.userAgent,
        currentUrl: window.location.pathname,
      });
      setSuccess(true);
    } catch (err: any) {
      setError('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={resetAndClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={resetAndClose}>
          &times;
        </button>

        <h2 className={styles.title}>Report a Bug</h2>

        {success ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>&#10003;</div>
            <div className={styles.successText}>Bug report submitted!</div>
            <div className={styles.successSubtext}>Thank you for helping improve Zodiac Chess.</div>
          </div>
        ) : (
          <>
            <p className={styles.description}>
              Found something broken? Let us know and we'll fix it.
            </p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Bug Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={styles.input}
                  placeholder="Brief title for the bug"
                  maxLength={100}
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>What happened? *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={styles.textarea}
                  placeholder="Describe the bug in detail..."
                  maxLength={1000}
                  rows={3}
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Steps to reproduce (optional)</label>
                <textarea
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  className={styles.textarea}
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  maxLength={1000}
                  rows={3}
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>What page were you on?</label>
                <select
                  value={page}
                  onChange={(e) => setPage(e.target.value)}
                  className={styles.select}
                >
                  <option value="">Select a page...</option>
                  {PAGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {error && <div className={styles.error}>{error}</div>}

              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading || !title.trim() || !description.trim()}
              >
                {loading ? 'Submitting...' : 'Submit Bug Report'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default BugReportModal;
